/**
 * QuickKeys — mounts the global keyboard shortcuts and the overlays they
 * open (command palette, shortcuts help). Rendered once inside the
 * authenticated tree in App.tsx.
 *
 *   Ctrl+K / ⌘K / "/"  → command palette (recents + search + actions)
 *   n                  → new work order
 *   g then l           → reopen the last opened work order
 *   ?                  → shortcuts help
 *
 * The header search box's keycap badge opens the palette too, via the
 * `openCommandPalette` CustomEvent, so the header doesn't need a prop path
 * to this component.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useKeyboardShortcut, useKeyboardChord } from '../ui/keyboard/useKeyboardShortcut';
import CommandPalette from './CommandPalette';
import KeyboardShortcutsHelp from './KeyboardShortcutsHelp';
import type { SearchSelection } from '../navigation/GlobalHeader';
import { readRecentWorkOrders } from '../../hooks/useRecentWorkOrders';

export const OPEN_COMMAND_PALETTE_EVENT = 'openCommandPalette';

interface QuickKeysProps {
  onSelect: (selection: SearchSelection) => void;
  /** Provided only when the current user/location can create work orders. */
  onCreateWorkOrder?: () => void;
  locationId?: string | null;
  /** The account in scope, for account settings results. */
  accountId?: string | null;
  /** Decides which accounts, locations and settings sections the palette offers. */
  permissions?: string[];
}

const QuickKeys: React.FC<QuickKeysProps> = ({ onSelect, onCreateWorkOrder, locationId, accountId, permissions }) => {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const openPalette = useCallback((e?: KeyboardEvent) => {
    e?.preventDefault();
    setHelpOpen(false);
    setPaletteOpen(true);
  }, []);

  // Ctrl+K / ⌘K toggles; works even while typing in a field (the modifier
  // prevents accidental fires) so it can be pressed from the header search.
  useKeyboardShortcut(
    'k',
    useCallback((e: KeyboardEvent) => {
      e.preventDefault();
      setHelpOpen(false);
      setPaletteOpen((open) => !open);
    }, []),
    { meta: true, ignoreInFields: false }
  );
  useKeyboardShortcut('/', openPalette);
  useKeyboardShortcut(
    '?',
    useCallback((e: KeyboardEvent) => {
      e.preventDefault();
      setPaletteOpen(false);
      setHelpOpen((open) => !open);
    }, []),
    { shift: true }
  );
  // Plain "n" — deliberately unmodified: browser-reserved combos (Ctrl+N =
  // new window, Ctrl+T = new tab) never reach the page, so a modified
  // new-work-order shortcut is impossible on the web. Close our own
  // overlays first so the create wizard doesn't open underneath them.
  useKeyboardShortcut(
    'n',
    useCallback(
      (e: KeyboardEvent) => {
        e.preventDefault();
        setPaletteOpen(false);
        setHelpOpen(false);
        onCreateWorkOrder?.();
      },
      [onCreateWorkOrder]
    ),
    { enabled: !!onCreateWorkOrder }
  );
  useKeyboardChord('g', {
    l: useCallback(() => {
      const last = readRecentWorkOrders()[0];
      if (last) onSelect({ type: 'work_order', id: last.id });
    }, [onSelect]),
  });

  // Header search badge (and any other mouse affordance) opens the palette
  useEffect(() => {
    const open = () => openPalette();
    window.addEventListener(OPEN_COMMAND_PALETTE_EVENT, open);
    return () => window.removeEventListener(OPEN_COMMAND_PALETTE_EVENT, open);
  }, [openPalette]);

  return (
    <>
      {/* Mount only while open so the closed palette doesn't subscribe to
          recents/storage events on every work-order open. */}
      {paletteOpen && (
        <CommandPalette
          isOpen={paletteOpen}
          onClose={() => setPaletteOpen(false)}
          onSelect={onSelect}
          onCreateWorkOrder={onCreateWorkOrder}
          locationId={locationId}
          accountId={accountId}
          permissions={permissions}
        />
      )}
      <KeyboardShortcutsHelp isOpen={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
};

export default QuickKeys;
